import { useState } from 'react';
import { useSessionStore } from '@/state/session-store';
import type { AgendaItem } from '@/types';

const PLACEHOLDER = `- Introduction and company overview
- Pain points we solve
- Product demo walkthrough
- Pricing and packaging
- Q&A and next steps`;

function PreviewItem({ item }: { item: AgendaItem }) {
  const indent = item.isGroupHeader ? 0 : item.level * 16;

  if (item.isGroupHeader) {
    return (
      <div className="mt-3 mb-1 first:mt-0">
        <div className="flex items-center gap-2 py-1.5 px-2 bg-gray-100 rounded-md border-l-4 border-l-blue-500">
          <span className="text-blue-600 font-semibold text-xs uppercase tracking-wide">{item.text}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-start gap-2 py-1.5 px-3 rounded-lg border-l-4 border-l-gray-200 bg-white"
      style={{ marginLeft: indent }}
    >
      <span className="text-gray-400 text-sm">○</span>
      <span className="text-gray-700 text-sm">{item.text}</span>
    </div>
  );
}

export function AgendaInput() {
  const { agenda, agendaRawText, setAgendaFromText } = useSessionStore();
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAgendaFromText(e.target.value);
  };

  const itemCount = agenda?.items.length || 0;
  const trackableCount = agenda?.items.filter(i => !i.isGroupHeader).length || 0;
  const headerCount = itemCount - trackableCount;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          Talking Points
        </label>
        {itemCount > 0 && (
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        )}
      </div>

      {showPreview && agenda ? (
        <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 min-h-[120px] max-h-[400px] overflow-y-auto">
          <div className="space-y-1">
            {agenda.items.map(item => (
              <PreviewItem key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
            {trackableCount} trackable item{trackableCount !== 1 ? 's' : ''}
            {headerCount > 0 && ` • ${headerCount} section header${headerCount !== 1 ? 's' : ''}`}
          </div>
        </div>
      ) : (
        <textarea
          value={agendaRawText}
          onChange={handleChange}
          placeholder={PLACEHOLDER}
          rows={10}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-400 resize-y min-h-[120px] max-h-[400px]"
        />
      )}

      {!showPreview && (
        <div className="mt-1 text-xs text-gray-500">
          {itemCount > 0 ? (
            <span className="text-green-600">{itemCount} item{itemCount !== 1 ? 's' : ''} ready</span>
          ) : (
            <span>Paste your agenda (one item per line)</span>
          )}
        </div>
      )}
    </div>
  );
}
